import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { RefreshCw, Sparkles } from 'lucide-react';

const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || 'dev';

/**
 * "Force update" trigger: nukes every Cache Storage bucket + unregisters
 * every SW + clears local/sessionStorage + reloads. Used from the gear menu
 * for users who somehow ended up on a stale bundle.
 */
export const ForceUpdateButton = ({ asMenuItem = false }) => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) { try { await r.unregister(); } catch (_) {} }
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        for (const k of keys) { try { await caches.delete(k); } catch (_) {} }
      }
      try { sessionStorage.clear(); } catch (_) {}
      // Note: deliberately preserve auth-related localStorage keys so user
      // doesn't get logged out — only nuke caches/SW state.
      setTimeout(() => { window.location.reload(); }, 400);
    } catch (e) { setRunning(false); }
  };

  const trigger = asMenuItem ? (
    <button
      onClick={() => setOpen(true)}
      className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted rounded-md flex items-center gap-2"
      data-testid="force-update-menuitem"
    >
      <RefreshCw size={12} /> Clear cache &amp; reload
    </button>
  ) : (
    <Button size="sm" variant="ghost" onClick={() => setOpen(true)} data-testid="force-update-btn">
      <RefreshCw size={12} className="mr-1" /> Clear cache &amp; reload
    </Button>
  );

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="force-update-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={16} className="text-violet-500" />Force update</DialogTitle>
            <DialogDescription>Clears the service-worker cache and reloads. Useful if you see a white screen or stale data after a deploy.</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-muted/50 p-3 text-xs">
            <div>Current build: <span className="font-mono font-bold">{BUILD_VERSION}</span></div>
            <div className="text-muted-foreground mt-0.5">Auth session is preserved — you won't be logged out.</div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={running}>Cancel</Button>
            <Button onClick={run} disabled={running} className="bg-violet-600 hover:bg-violet-700" data-testid="force-update-confirm">
              {running ? 'Working…' : 'Clear & reload'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ForceUpdateButton;
